import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Users, Euro, ArrowLeft, Expand } from 'lucide-react';
import axios from 'axios';
import { AvailabilityCalendar } from '@/components/AvailabilityCalendar';
import { Reviews } from '@/components/Reviews';
import { Lightbox } from '@/components/Lightbox';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RoomDetail = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const galleryImages = room?.images && room.images.length > 0 ? room.images : (room?.image_url ? [room.image_url] : []);

  useEffect(() => {
    fetchRoom();
  }, [roomId]);

  const fetchRoom = async () => {
    try {
      const response = await axios.get(`${API}/rooms/${roomId}`);
      setRoom(response.data);
    } catch (error) {
      console.error('Error fetching room:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <p className="text-[#8A9A86] text-xl">{t('common.loading')}</p>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <p className="text-[#2C302E] text-xl">Room not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <Button
          data-testid="back-to-rooms"
          variant="ghost"
          onClick={() => navigate('/rooms')}
          className="mb-6 text-[#2C302E]/70 hover:text-[#8A9A86]"
        >
          <ArrowLeft size={16} className="mr-2" />
          {language === 'it' ? 'Torna alle camere' : 'Back to rooms'}
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {room.images && room.images.length >= 2 ? (
            <div className="mb-8 space-y-3" data-testid="room-gallery">
              {/* Main hero photo - full width, aspect matches source photo ratio */}
              <div 
                className="rounded-lg overflow-hidden aspect-[4/3] bg-[#F0F2EB] cursor-pointer group relative"
                onClick={() => openLightbox(0)}
                data-testid="gallery-main-image"
              >
                <img
                  src={room.images[0]}
                  alt={language === 'it' ? room.name_it : room.name_en}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  data-testid="room-detail-image"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="bg-white/90 backdrop-blur-sm rounded-full p-4">
                    <Expand size={24} className="text-[#2C302E]" />
                  </div>
                </div>
              </div>
              {/* Secondary photos - grid based on count */}
              {room.images.length > 1 && (
                <div className={`grid gap-3 ${
                  room.images.length === 2 ? 'grid-cols-1' :
                  room.images.length === 3 ? 'grid-cols-2' :
                  'grid-cols-2 md:grid-cols-4'
                }`}>
                  {room.images.slice(1, 5).map((img, idx) => (
                    <div 
                      key={idx} 
                      className="rounded-lg overflow-hidden aspect-[4/3] bg-[#F0F2EB] cursor-pointer group relative"
                      onClick={() => openLightbox(idx + 1)}
                      data-testid={`gallery-thumb-${idx}`}
                    >
                      <img
                        src={img}
                        alt={`${language === 'it' ? room.name_it : room.name_en} ${idx + 2}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div 
              className="rounded-lg overflow-hidden mb-8 aspect-[4/3] bg-[#F0F2EB] cursor-pointer group relative"
              onClick={() => openLightbox(0)}
              data-testid="gallery-main-image"
            >
              <img
                src={room.image_url}
                alt={language === 'it' ? room.name_it : room.name_en}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                data-testid="room-detail-image"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="bg-white/90 backdrop-blur-sm rounded-full p-4">
                  <Expand size={24} className="text-[#2C302E]" />
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-2">
              <h1 
                className="text-4xl sm:text-5xl font-light tracking-tight text-[#2C302E] mb-4"
                style={{fontFamily: 'Cormorant Garamond, serif'}}
                data-testid="room-detail-title"
              >
                {language === 'it' ? room.name_it : room.name_en}
              </h1>

              <div className="flex items-center gap-6 mb-6 text-sm text-[#2C302E]/60">
                <div className="flex items-center gap-2">
                  <Users size={18} className="text-[#8A9A86]" />
                  <span>{room.max_guests} {t('rooms.guests')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Euro size={18} className="text-[#8A9A86]" />
                  <span className="font-medium text-[#2C302E]">€{room.price} / {t('rooms.perNight')}</span>
                </div>
              </div>

              <p 
                className="text-[#2C302E]/80 leading-relaxed text-lg mb-6"
                style={{fontFamily: 'Manrope, sans-serif'}}
              >
                {language === 'it' ? room.description_it : room.description_en}
              </p>

              {(language === 'it' ? room.amenities_it : room.amenities_en)?.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-medium text-[#2C302E] mb-3">{t('rooms.amenities')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {(language === 'it' ? room.amenities_it : room.amenities_en).map((amenity, i) => (
                      <span
                        key={i}
                        className="text-sm px-4 py-2 bg-[#F0F2EB] text-[#2C302E] rounded-full"
                      >
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <Button
                data-testid="book-this-room-button"
                onClick={() => navigate(`/booking/${roomId}`)}
                className="bg-[#8A9A86] hover:bg-[#7A8A76] text-white rounded-full px-10 py-6 text-lg transition-transform hover:scale-105"
              >
                {t('rooms.bookRoom')}
              </Button>
            </div>

            <div className="lg:col-span-1">
              <h3 
                className="text-2xl font-light text-[#2C302E] mb-4"
                style={{fontFamily: 'Cormorant Garamond, serif'}}
              >
                {language === 'it' ? 'Disponibilità' : 'Availability'}
              </h3>
              <AvailabilityCalendar roomId={roomId} />
            </div>
          </div>

          <div className="border-t border-[#E3E5DF] pt-12">
            <Reviews roomId={roomId} />
          </div>
        </motion.div>
      </div>

      <Lightbox
        images={galleryImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        alt={language === 'it' ? room.name_it : room.name_en}
      />
    </div>
  );
};
